import { LoadingButton } from '@mui/lab';
import {
  Button,
  Stack,
  Typography,
  Box,
  Divider,
  Card,
  CardContent,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from '@mui/material';
import TessellModal from 'common/custom-components/lib/components/TessellModal';
import { useState, useEffect } from 'react';
import DatePickerToggler from 'common/custom-components/lib/components/DatePickerToggler';
import moment from 'moment';
import { useOSPatchSilenceModal } from './useOSPatchSilenceModal';

type ServiceData = {
  id?: string;
  name?: string;
  status?: string;
  [key: string]: any;
};

const rangeType = {
  '1h': '1 hour',
  '3h': '3 hours',
  '6h': '6 hours',
  '12h': '12 hours',
  '24h': '24 hours',
  Custom: 'Custom',
};

const startMin = moment(new Date());

type OSPatchSilenceModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  dBServicesRows?: any[];
  computeResourceId?: string;
  tenantId?: string;
  tenantDomain?: string;
};

export const OSPatchSilenceModal = (props: OSPatchSilenceModalProps) => {
  const {
    open,
    setOpen,
    dBServicesRows,
    computeResourceId,
    tenantId = '',
    tenantDomain = '',
  } = props;

  // Time selection state
  const [timeInputs, setTimeInputs] = useState(() => {
    const now = moment();
    return {
      startsAt: now,
      endsAt: now.clone().add(3, 'hours'),
    };
  });
  const [range, setRange] = useState('3h');

  // Reset state when modal opens to ensure fresh state
  useEffect(() => {
    if (open) {
      const now = moment();
      setTimeInputs({
        startsAt: now,
        endsAt: now.clone().add(3, 'hours'),
      });
      setRange('3h');
    }
  }, [open]);

  // Process dBServicesRows data directly in the modal
  const validServices = dBServicesRows?.filter((r) => !!r) || [];
  const totalServicesCount = validServices.length;
  const serviceData = validServices.length > 0 ? validServices[0] : null;

  const { handleSilence, isLoading } = useOSPatchSilenceModal({
    services: validServices,
    computeResourceId,
    onSuccess: () => {
      setOpen(false);
    },
    onError: (error) => {
      console.error('OS Patch Silence Modal Error:', error);
      // You could show an error notification here
    },
    tenantId,
    tenantDomain,
  });

  // Time selection handlers
  const handleDateToggle = (e: any, v: string) => {
    if (v) {
      setRange(v);

      // Predefined range selection
      const now = moment();
      const start = now;
      let end;

      switch (v) {
        case '1h':
          end = moment().add(1, 'hour');
          break;
        case '6h':
          end = moment().add(6, 'hours');
          break;
        case '12h':
          end = moment().add(12, 'hours');
          break;
        case '24h':
          end = moment().add(24, 'hours');
          break;
        case '3h':
        default:
          end = moment().add(3, 'hours');
          break;
      }

      setTimeInputs({
        startsAt: start,
        endsAt: end,
      });
    }
  };

  const updateTimeUsingNativePicker = (startTime: any, endTime: any) => {
    setTimeInputs({
      startsAt: moment(startTime),
      endsAt: moment(endTime),
    });
  };

  const handleSubmit = async () => {
    try {
      await handleSilence(
        timeInputs.startsAt.toISOString(),
        timeInputs.endsAt.toISOString(),
      );
    } catch (error) {
      // Error handling is done in the hook
      console.error('Modal - Failed to silence OS patches:', error);
    }
  };

  const onClose = () => {
    setOpen(false);
  };

  const DialogTitle = () => (
    <Typography variant="h5">OS Patch Silence</Typography>
  );

  const ServicesListTable = () => {
    const servicesToShow = validServices;

    if (servicesToShow.length === 0) {
      return (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" color="text.secondary">
              No Services Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No services available in the current view.
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" color="primary.main" sx={{ mb: 2 }}>
            Services to be Affected ({servicesToShow.length})
          </Typography>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Service Name</strong>
                  </TableCell>
                  <TableCell>
                    <strong>ID</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {servicesToShow.map((service, index) => (
                  <TableRow key={service.id || index} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {service.name || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {service.id || 'N/A'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  const DialogContent = () => {
    return (
      <Stack spacing={3}>
        <Typography variant="body1" color="text.secondary">
          {totalServicesCount
            ? `Review the details below before silencing OS patch notifications for ${totalServicesCount} service${
                totalServicesCount > 1 ? 's' : ''
              } currently displayed in the table view.`
            : 'Review the details below before silencing OS patch notifications for this infrastructure.'}
        </Typography>

        {totalServicesCount > 1 && (
          <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="body2" color="warning.contrastText">
              <strong>Bulk Action Notice:</strong> This action will affect all{' '}
              {totalServicesCount} services currently visible in the table. The
              details shown below are from a representative service in your
              current view.
            </Typography>
          </Box>
        )}

        <Divider />

        <ServicesListTable />

        <Stack direction="row" spacing={1} sx={{ mt: 3, alignItems: 'center' }}>
          <Typography variant="body1" fontWeight="medium">
            Silence OS patches for:
          </Typography>
          <DatePickerToggler
            key={`datepicker-${open}-${range}`}
            rangeType={rangeType}
            startTime={timeInputs.startsAt}
            endTime={timeInputs.endsAt}
            updateTimeUsingNativePicker={updateTimeUsingNativePicker}
            startMin={startMin}
            handleOnChange={handleDateToggle}
            range={range}
            additionalInitialSettings={{ drops: 'up' }}
          />
        </Stack>

        <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1, mt: 2 }}>
          <Typography variant="body2" color="warning.contrastText">
            <strong>Warning:</strong> OS patch silence affects alerts for both
            the <strong>server/compute</strong> and{' '}
            <strong>
              {totalServicesCount} service{totalServicesCount !== 1 ? 's' : ''}
            </strong>{' '}
            mentioned above.
          </Typography>
        </Box>
      </Stack>
    );
  };

  const DialogActions = () => {
    return (
      <Stack direction="row" spacing={2}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="medium"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          size="medium"
          variant="contained"
          loading={isLoading}
          color="warning"
        >
          Silence OS Patches
        </LoadingButton>
      </Stack>
    );
  };

  const renderFunctions = {
    title: DialogTitle,
    content: DialogContent,
    actions: DialogActions,
  };

  return (
    <TessellModal
      renderFunctions={renderFunctions}
      maxWidth="md"
      open={open}
      onClose={onClose}
    />
  );
};
